import {ResizeObserver as ObserverPolyfill} from '@juggle/resize-observer'

declare global {
  interface Window {
    IS_CODESPACES: boolean
  }
}

// Safari < 14 and some earlier versions of firefox don't support resize observer natively
const Observer = 'ResizeObserver' in window ? window.ResizeObserver : ObserverPolyfill

function defaultResize(height: number): void {
  // eslint-disable-next-line no-console
  console.log('ResizeObserverHeightTracker: default resize handler called', height)
  return
}
/**
 * We use the resize observer to detect changes to the height of the target node.
 * This allows us to get the actual size of that node after painting content to the DOM
 * Previously we were seeing a race condition between the DOM elements being inserted
 * ( but not painted ), and our resize code.
 */
class ResizeObserverHeightTracker {
  nodeHeight: number
  lastResize: number | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resizeObserver: any
  node: HTMLElement
  isDisconnected: boolean
  onResize: (height: number) => void

  constructor({node = document.body, onResize = defaultResize} = {}) {
    this.node = node
    this.nodeHeight = 0
    this.lastResize = null
    this.isDisconnected = false
    this.onResize = onResize

    this.resizeObserver = new Observer(entries => {
      /**
       * It is possible to disconnect the observer while it is in the middle
       * of handling a resize event, which will throw an error in FireFox. While
       * this error doesn't (seem to) materially affect the app's performance,
       * it does trigger sentry errors. Therefore, we'll try soft disconnecting
       * when the observer's job is finished, and simply ignore future events.
       *
       * There are many flavors of answer about this issue, but here is one from
       * an author of the spec: https://stackoverflow.com/a/50387233/1108934
       */
      if (this.isDisconnected) {
        return
      }

      for (const entry of entries) {
        const height = entry.contentRect.height
        const nextHeight = window.IS_CODESPACES ? Math.max(height, 800) : height

        this.nodeHeight = nextHeight
        this.lastResize = performance.now()

        this.onResize?.(this.nodeHeight)
      }
    })
  }

  observe() {
    this.resizeObserver.observe(this.node)
  }

  disconnect() {
    this.isDisconnected = true
  }
}

export default ResizeObserverHeightTracker
